import { useState, useEffect } from 'react';
import fileApi from '../../../services/file.service';
import type { MessageAttachment } from '../../../types/chat.types';

interface UseMessageAttachmentUrlResult {
  url: string | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to get the display URL for a message attachment.
 * Fetches a presigned URL from the file service using the fileId.
 */
export function useMessageAttachmentUrl(
  attachment: MessageAttachment
): UseMessageAttachmentUrlResult {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // If we have a fileId, fetch the presigned URL
    if (attachment.fileId) {
      setLoading(true);
      setError(null);

      fileApi
        .getDownloadUrl(attachment.fileId)
        .then((downloadUrl) => {
          setUrl(downloadUrl);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to fetch file download URL:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch download URL'));
          setUrl(null);
          setLoading(false);
        });
    } else {
      // No fileId available
      setUrl(null);
      setLoading(false);
    }
  }, [attachment.fileId]);

  return { url, loading, error };
}
